import {
  Container,
  Group,
  Title,
  Text,
  Table,
  Badge,
  Card,
  SimpleGrid,
  Stack,
  Skeleton,
  RingProgress,
  Center,
} from '@mantine/core';
import { IconTrendingUp, IconTrendingDown, IconArrowUp, IconArrowDown } from '@tabler/icons-react';
import { useSatisfactionScore, useSatisfactionHistory, useSatisfactionActions } from '@/hooks/usePortalApi';

const QUADRANT_COLORS: Record<string, string> = {
  champion: 'green',
  loyal: 'blue',
  at_risk: 'yellow',
  churning: 'red',
};

function toArray(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    // Handle {items: [...]} or {scores: [...]} or {actions: [...]} etc.
    for (const val of Object.values(data as Record<string, unknown>)) {
      if (Array.isArray(val)) return val;
    }
  }
  return [];
}

export default function SatisfactionDashboard() {
  const { data: scoreData, isLoading: scoreLoading } = useSatisfactionScore();
  const { data: rawHistory, isLoading: historyLoading } = useSatisfactionHistory();
  const { data: rawActions, isLoading: actionsLoading } = useSatisfactionActions();
  const history = toArray(rawHistory);
  const actions = toArray(rawActions);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'blue';
    if (score >= 40) return 'yellow';
    return 'red';
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>My Satisfaction Score</Title>
        </div>
      </Group>

      {scoreLoading ? (
        <Skeleton height={200} mb="md" />
      ) : scoreData ? (
        <Card withBorder mb="md" p="xl">
          <Center>
            <Stack align="center" gap="md">
              <RingProgress
                size={200}
                thickness={20}
                sections={[{ value: scoreData.score, color: getScoreColor(scoreData.score) }]}
                label={
                  <Center>
                    <Stack align="center" gap={0}>
                      <Text size="xl" fw={700}>{scoreData.score}</Text>
                      <Text size="xs" c="dimmed">out of 100</Text>
                    </Stack>
                  </Center>
                }
              />
              <Badge
                size="xl"
                color={QUADRANT_COLORS[scoreData.quadrant] || 'gray'}
                variant="light"
              >
                {scoreData.quadrant_label}
              </Badge>
            </Stack>
          </Center>
        </Card>
      ) : null}

      <Title order={3} mb="md">Trends</Title>
      {scoreLoading ? (
        <SimpleGrid cols={3} mb="md">
          <Skeleton height={120} />
          <Skeleton height={120} />
          <Skeleton height={120} />
        </SimpleGrid>
      ) : scoreData ? (
        <SimpleGrid cols={3} mb="md">
          <Card withBorder>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">Score Trend</Text>
              <Group gap="xs" align="center">
                <Text size="xl" fw={700}>{scoreData.score}</Text>
                {scoreData.score_change !== undefined && (
                  <Group gap={4}>
                    {scoreData.score_change >= 0 ? (
                      <IconArrowUp size={16} color="green" />
                    ) : (
                      <IconArrowDown size={16} color="red" />
                    )}
                    <Text size="sm" c={scoreData.score_change >= 0 ? 'green' : 'red'}>
                      {Math.abs(scoreData.score_change)}
                    </Text>
                  </Group>
                )}
              </Group>
            </Stack>
          </Card>

          <Card withBorder>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">Order Frequency</Text>
              <Group gap="xs" align="center">
                <Text size="xl" fw={700}>{scoreData.order_frequency}</Text>
                <Text size="sm" c="dimmed">orders/month</Text>
              </Group>
              {scoreData.frequency_change !== undefined && (
                <Group gap={4}>
                  {scoreData.frequency_change >= 0 ? (
                    <IconTrendingUp size={16} color="green" />
                  ) : (
                    <IconTrendingDown size={16} color="red" />
                  )}
                  <Text size="xs" c={scoreData.frequency_change >= 0 ? 'green' : 'red'}>
                    {scoreData.frequency_change >= 0 ? '+' : ''}{scoreData.frequency_change}%
                  </Text>
                </Group>
              )}
            </Stack>
          </Card>

          <Card withBorder>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">Payment Timeliness</Text>
              <Group gap="xs" align="center">
                <Text size="xl" fw={700}>{scoreData.payment_timeliness}%</Text>
              </Group>
              {scoreData.payment_change !== undefined && (
                <Group gap={4}>
                  {scoreData.payment_change >= 0 ? (
                    <IconTrendingUp size={16} color="green" />
                  ) : (
                    <IconTrendingDown size={16} color="red" />
                  )}
                  <Text size="xs" c={scoreData.payment_change >= 0 ? 'green' : 'red'}>
                    {scoreData.payment_change >= 0 ? '+' : ''}{scoreData.payment_change}%
                  </Text>
                </Group>
              )}
            </Stack>
          </Card>
        </SimpleGrid>
      ) : null}

      <Title order={3} mb="md">Score History</Title>
      {historyLoading ? (
        <Stack gap="xs">
          <Skeleton height={50} />
          <Skeleton height={50} />
          <Skeleton height={50} />
        </Stack>
      ) : !history || history.length === 0 ? (
        <Text ta="center" py="xl" c="dimmed">No score history available</Text>
      ) : (
        <Table striped highlightOnHover mb="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th>Score</Table.Th>
              <Table.Th>Change</Table.Th>
              <Table.Th>Quadrant</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {history.map((entry: any, idx: number) => (
              <Table.Tr key={idx}>
                <Table.Td>{new Date(entry.date).toLocaleDateString()}</Table.Td>
                <Table.Td>
                  <Badge color={getScoreColor(entry.score)} variant="light">
                    {entry.score}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {entry.change !== undefined && entry.change !== 0 && (
                    <Group gap={4}>
                      {entry.change > 0 ? (
                        <IconArrowUp size={14} color="green" />
                      ) : (
                        <IconArrowDown size={14} color="red" />
                      )}
                      <Text size="sm" c={entry.change > 0 ? 'green' : 'red'}>
                        {entry.change > 0 ? '+' : ''}{entry.change}
                      </Text>
                    </Group>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge color={QUADRANT_COLORS[entry.quadrant] || 'gray'} variant="light">
                    {entry.quadrant_label}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Title order={3} mb="md">Pending Actions</Title>
      {actionsLoading ? (
        <Stack gap="xs">
          <Skeleton height={50} />
          <Skeleton height={50} />
        </Stack>
      ) : !actions || actions.length === 0 ? (
        <Text ta="center" py="xl" c="dimmed">No pending actions</Text>
      ) : (
        <Stack gap="md">
          {actions.map((action: any, idx: number) => (
            <Card key={idx} withBorder>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={600}>{action.title}</Text>
                  <Badge color={action.priority === 'high' ? 'red' : 'blue'} variant="light">
                    {action.priority}
                  </Badge>
                </Group>
                <Text size="sm">{action.description}</Text>
                {action.due_date && (
                  <Text size="xs" c="dimmed">Due: {new Date(action.due_date).toLocaleDateString()}</Text>
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </Container>
  );
}
